export const services = [
  // ——— Consulting ———
  {
    slug: "brand-strategy",
    title: "Brand Strategy",
    group: "Consulting",
    tagline: "The groundwork that compounds for decades.",
    bg: "#F2C14E",
    fg: "#1C1C19",
    accent: "#E26D5C",
    summary:
      "Positioning, naming, identity systems and narrative work. We embed senior strategists into portfolio companies for focused sprints to deliver the strategic foundations that the next stretch of growth gets built on top of.",
    offerings: [
      {
        title: "Positioning",
        body:
          "Who you are, who you're for, and the single sentence you want to be known for.",
      },
      {
        title: "Identity systems",
        body:
          "Logo, type, colour, photographic direction, and a system flexible enough to live across stores, screens and shelves.",
      },
      {
        title: "Naming",
        body:
          "Brand names, product names, sub-brand architecture, and the trademark work to actually own them.",
      },
      {
        title: "Narrative & voice",
        body:
          "The story your founders tell, the way your packaging speaks, and the words your customer service uses.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Listen",
        body: "Founder interviews, customer ride-alongs, competitor teardown.",
      },
      {
        step: "02",
        title: "Frame",
        body:
          "We compress everything into one positioning document worth fighting for.",
      },
      {
        step: "03",
        title: "Make",
        body: "Identity, system, narrative, built in-house, never offshored.",
      },
      {
        step: "04",
        title: "Hand-off",
        body: "Toolkits, guidelines and check-ins after launch.",
      },
    ],
    cta: "Start a brand sprint",
    image:
      "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&w=1600&q=80",
  },
  {
    slug: "marketing-strategy",
    title: "Marketing Strategy",
    group: "Consulting",
    tagline: "Clarity over noise, always.",
    bg: "#E26D5C",
    fg: "#F7F5F0",
    accent: "#F2C14E",
    summary:
      "Channel prioritisation, budget allocation, and a roadmap leadership can actually act on.",
    offerings: [
      {
        title: "Channel mix",
        body:
          "Where attention and money actually go today, and which few channels are worth the next rupee. We separate the noise from the channels that move the business.",
      },
      {
        title: "Budget allocation",
        body:
          "A budget split by evidence, not habit. We put spend where it compounds and cut the line items that only look busy.",
      },
      {
        title: "Roadmapping",
        body:
          "A quarter-by-quarter plan leadership can act on, with clear owners, sequencing, and the trade-offs made explicit.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Map",
        body:
          "We map where attention and budget go today, channel by channel, with the numbers behind each.",
      },
      {
        step: "02",
        title: "Prioritise",
        body:
          "We name the few channels worth the budget, and the ones to stop feeding.",
      },
      {
        step: "03",
        title: "Sequence",
        body:
          "We turn priorities into a roadmap with owners, timing, and honest trade-offs.",
      },
      {
        step: "04",
        title: "Hand-off",
        body: "Your team owns the plan; we stay close through the first cycle.",
      },
    ],
    cta: "Map your channels",
    image:
      "https://images.unsplash.com/photo-1532619675605-1ede6c2ed2b0?auto=format&fit=crop&w=1600&q=80",
  },
  {
    slug: "go-to-market",
    title: "Go to Market Planning",
    group: "Consulting",
    tagline: "Earn the second launch with the first.",
    bg: "#1C1C19",
    fg: "#F7F5F0",
    accent: "#F2C14E",
    summary:
      "Getting the sequence right, so the first launch earns the second.",
    offerings: [
      {
        title: "Launch sequencing",
        body:
          "The order of moves that lets each launch fund the next. We plan what ships first, what waits, and why.",
      },
      {
        title: "Market entry",
        body:
          "How you show up in a new market, region, or channel without diluting the brand or the margins.",
      },
      {
        title: "Category strategy",
        body:
          "The category you choose to compete in, and the position that makes the choice feel obvious to customers.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Position",
        body:
          "We settle the category and the position before a single date goes on the calendar.",
      },
      {
        step: "02",
        title: "Sequence",
        body:
          "We plan the order of launches so the first earns the room for the second.",
      },
      {
        step: "03",
        title: "Launch",
        body:
          "We run the first move end to end, with the team aligned on the one thing that matters.",
      },
      {
        step: "04",
        title: "Learn",
        body:
          "We read what the market actually did, and fold it into the next launch.",
      },
    ],
    cta: "Plan the launch",
    image:
      "https://images.unsplash.com/photo-1516849841032-87cbac4d88f7?auto=format&fit=crop&w=1600&q=80",
  },
  // ——— Growth Partner ———
  {
    slug: "creative",
    title: "Creative",
    group: "Growth Partner",
    tagline: "An in-house studio that ships.",
    bg: "#2A3439",
    fg: "#F7F5F0",
    accent: "#F2C14E",
    summary:
      "A network of designers, directors, copywriters and producers, assembled around your brand and the work it needs.",
    offerings: [
      {
        title: "Campaigns",
        body:
          "Concept, art direction, production and rollout across film, print, OOH and digital, handled end to end.",
      },
      {
        title: "Film & motion",
        body:
          "Brand films, product launches, documentary-led content, and the social cutdowns to feed every channel.",
      },
      {
        title: "Packaging",
        body:
          "Structural and surface design, prototyping, sustainable material work, and the production oversight to ship it.",
      },
      {
        title: "Retail & web",
        body:
          "Store design, in-store experience, e-commerce builds and the visual systems that tie them together.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Brief",
        body: "We co-write it with you. Sloppy briefs make sloppy work.",
      },
      {
        step: "02",
        title: "Concept",
        body: "A few sharp directions; never a deck of mediocre options.",
      },
      {
        step: "03",
        title: "Make",
        body: "We design, shoot, produce and project-manage the whole thing.",
      },
      {
        step: "04",
        title: "Ship",
        body: "Launch support, measurement, and a hand-off doc for your team.",
      },
    ],
    cta: "Brief the studio",
    image:
      "https://images.unsplash.com/photo-1558655146-9f40138edfeb?auto=format&fit=crop&w=1600&q=80",
  },
  {
    slug: "growth",
    title: "Growth & Performance",
    group: "Growth Partner",
    tagline: "Senior operators, not interns.",
    bg: "#729B79",
    fg: "#F7F5F0",
    accent: "#F2C14E",
    summary:
      "The engine that turns a strategy into revenue, run by people who've built it inside brands you've heard of.",
    offerings: [
      {
        title: "Paid acquisition",
        body:
          "Meta, TikTok, search, programmatic and connected TV, measured at the unit level, not the campaign level.",
      },
      {
        title: "Retention & CRM",
        body:
          "Lifecycle, loyalty, owned channels and the analytics stack to make every campaign smarter than the last.",
      },
      {
        title: "Retail expansion",
        body:
          "From department-store door strategy to specialty placement, including the merchandising and training to make it work.",
      },
      {
        title: "DTC engineering",
        body:
          "Shopify and headless builds, on-site personalisation, pricing experiments, and the analytics behind them.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Audit",
        body:
          "A focused period digging through your data, agencies, and tech stack.",
      },
      {
        step: "02",
        title: "Plan",
        body: "A growth plan, channel by channel, with honest budgets.",
      },
      {
        step: "03",
        title: "Operate",
        body: "Embedded lead runs it, hires for it, ships it with your team.",
      },
      {
        step: "04",
        title: "Hand-off",
        body: "Your team owns the engine; we step into an advisory cadence.",
      },
    ],
    cta: "Book a growth audit",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1600&q=80",
  },
  {
    slug: "brand-build",
    title: "Brand Build & Rollout",
    group: "Growth Partner",
    tagline: "Where strategy becomes what customers see.",
    bg: "#E6E3DB",
    fg: "#1C1C19",
    accent: "#729B79",
    summary:
      "Where the strategy stops being a document and starts being what customers actually see.",
    offerings: [
      {
        title: "Identity rollout",
        body:
          "Taking the identity from a guidelines document to every touchpoint, consistently, across every place the brand appears.",
      },
      {
        title: "Campaign launch",
        body:
          "The launch that puts the new brand in front of customers, with the creative and the plan to make it land.",
      },
      {
        title: "In-market execution",
        body:
          "The unglamorous work of keeping the brand right in stores, on screens, and in the hands of the people who represent it.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Prepare",
        body:
          "We turn the strategy and identity into a rollout plan every team can follow.",
      },
      {
        step: "02",
        title: "Build",
        body:
          "We produce the assets, templates, and systems the brand needs to go live.",
      },
      {
        step: "03",
        title: "Launch",
        body:
          "We put the brand in market, in the order and channels that give it the best start.",
      },
      {
        step: "04",
        title: "Sustain",
        body:
          "We set up the guardrails and cadence that keep the brand consistent after launch.",
      },
    ],
    cta: "Roll it out",
    image:
      "https://images.unsplash.com/photo-1770902971692-e4b9e3cf3933?auto=format&fit=crop&w=1600&q=80",
  },
  // ——— Hidden: bring back by setting hidden: false ———
  {
    slug: "capital",
    title: "GroundWork Capital",
    group: "Growth Partner",
    hidden: true,
    tagline: "Coming soon.",
    bg: "#E6E3DB",
    fg: "#1C1C19",
    accent: "#729B79",
    comingSoon: true,
    summary:
      "The natural extension of GroundWork's work with founders: moving from advising on brand equity to deploying capital behind it.",
    body: [
      "GroundWork Capital will invest in Indian D2C brands where the fundamentals (positioning, identity, and demand) are already proven, applying the same 'brand before performance' discipline we use with every client, backed by capital instead of only strategy.",
      "This is in active development. We're building the operating business first, proving the model with real client work, before formalizing the fund.",
      "If you're a founder or an investor interested in what this becomes, reach out.",
    ],
    cta: "Reach out",
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1600&q=80",
  },
];

export const visibleServices = services.filter((s) => !s.hidden);

export const serviceGroups = ["Consulting", "Growth Partner"];

export const getServiceBySlug = (slug) =>
  services.find((s) => s.slug === slug);
